import React, { useCallback, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

import BasePage from "./BasePage";
import ErrorMessage from "./ErrorMessage";
import { API_URL } from "./config";
import { trackError } from "./beacon";
import { useParams } from "react-router-dom";
import { useApp } from "./App";

function ClickableItem({ item }: { item: ClickableLink }) {
  return (
    <a
      style={{
        display: "block",
        width: "100%",
        backgroundColor: "#fff",
        color: "#000",
        padding: "10px 15px",
        margin: "5px 0",
        textDecoration: "none",
        border: "1px solid #ddd",
        borderRadius: "5px",
        textAlign: "center",
      }}
      className="hover:bg-gray-200 transition-colors duration-200"
      href={item.url}
    >
      {item.title}
    </a>
  );
}


// ⚠ THIS MODEL DESCRIBED A RESPONSE THE API HAS NEVER SENT, AND THE SCREEN CRASHED ON EVERY
// WAIVER EVER SIGNED. It read `data.data.form.firstName`; `/retrieve-waiver` returned
// `{data: {waiver: {first_name, last_name, email, phone_number, shop, waiver_id,
// created_at_timestamp, link_to_waiver}, links: [...]}}`. `data.data` was undefined, so the
// constructor fell to `new WaiverData({})`, which handed `undefined` to WaiverFormData, which
// died on `.firstName` — a white page with three skeleton bars, for the client, silently.
//
// Measured 2026-09-08 against BOTH ends of the table: a 2024-02-13 record and the newest one that
// day return the identical shape, so this was never an old-data problem. Nobody saw it because
// nothing sends a client this link yet — TW-0110 would have been the first, and it would have
// shipped straight into this.
//
// Since 2026-09-14 (TW-0167) the waiver carries only the four fields the model below reads: the
// email, the phone, the shop and the storage link are gone from the answer, and the "View completed
// waiver" link is our own file door (app.waivers.tattoo/api/waiver-file?t=<token>), never storage.
//
// snake_case is what the API speaks; keep it that way here rather than translating, so the next
// reader can diff this file against the handler instead of a mapping in someone's head.
class Waiver {
  first_name: string;
  last_name: string;
  waiver_id: string;
  created_at_timestamp: number | null;

  constructor(data: any) {
    const d = data || {};
    this.first_name = d.first_name || "";
    this.last_name = d.last_name || "";
    this.waiver_id = d.waiver_id || "";
    this.created_at_timestamp = d.created_at_timestamp ?? null;
  }
}

class ClickableLink {
  title: string;
  url: string;

  constructor(data: any) {
    this.title = data.title;
    this.url = data.url;
  }
}

function WaiverDetail() {

  const {
    client,
  } = useApp();

  const { waiver_unique_id: waiverUniqueId } = useParams<{ waiver_unique_id: string }>();

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [waiver, setWaiver] = useState<Waiver | null>(null);
  const [clickableLinks, setClickableLinks] = useState<ClickableLink[]>([]);

  const loadWaiver = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    setWaiver(null);

    fetch(`${API_URL}/retrieve-waiver?waiver_unique_id=${waiverUniqueId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (res.status === 200) {
          // Success
          res
            .json()
            .then((data) => {
              const updatedWaiver = new Waiver(data?.data?.waiver);
              setWaiver(updatedWaiver);
              setClickableLinks(
                (data?.data?.links || []).map((link: any) => new ClickableLink(link))
              );
              setLoading(false);
            })
            .catch((err) => {
              // ⚠ THE BUG THAT MINTED TW-0132 CAME THROUGH HERE, and reported nothing.
              //
              // The rejection is HANDLED, so it is not an unhandledrejection and it is not a render
              // throw: neither the route's errorElement nor the global handlers installed in
              // index.tsx can see it. And because this was the one catch in this file that set
              // neither the message nor `loading`, the page kept rendering three skeleton bars
              // forever — the "white card with three skeleton bars" a shop described. Its three
              // siblings below always did both; only the SUCCESS path forgot, which is why it
              // survived every reading of this file.
              trackError((err as Error)?.message || "waiver detail: the body did not parse", {
                source: "WaiverDetail.parseSuccess",
                stack: (err as Error)?.stack,
              });
              setErrorMessage("There was an error retrieving your waiver.");
              setLoading(false);
            });
        } else {
          res
            .json()
            .then((data) => {
              setErrorMessage(
                data?.error?.message ||
                "There was an error retrieving your waiver (501)."
              );
              setLoading(false);
            })
            .catch((err) => {
              // Error
              setErrorMessage(
                "There was an error retrieving your waiver (500)."
              );
              setLoading(false);
            });
        }
      })
      .catch((err) => {
        console.log("err", err);
        setErrorMessage("There was an error retrieving your waiver.");
        setLoading(false);
      });
  }, [waiverUniqueId]);

  useEffect(() => {
    loadWaiver();
  }, [loadWaiver]);

  return (
    <BasePage>
      <div>
        {client && client.public_data?.logo_url && <a href="/">
          <img
            src={client.public_data.logo_url}
            alt={client.name}
            className="mx-auto"
            style={{ width: 100 }}
          />
        </a>}
        {loading ? (
          <>
            <Skeleton count={3} height={46} style={{ margin: "5px 0" }} />
          </>
        ) : (
          <>
            {errorMessage || !waiver ? (
              <div className="mb-8">
                <ErrorMessage
                  message={errorMessage || "Something went wrong."}
                />
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="px-4 pt-4 pb-4">
                    <h1 className="text-2xl font-bold">
                      {waiver.first_name ? `Thank you, ${waiver.first_name}!` : 'Thank you!'}
                    </h1>
                  </div>
                  <div className="flex flex-col items-center">
                    {clickableLinks.map((link, index) => (
                      <ClickableItem key={`item-${index}`} item={link} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </BasePage>
  );
}

export default WaiverDetail;
